import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { SITE_PHONE_DISPLAY, SITE_PHONE_TEL } from "../../config/site";

const navLinks = [
  { label: "Accident Types", href: "/accident-types" },
  { label: "Indiana Cities", href: "/indiana-cities" },
  { label: "Resources", href: "/resources" },
  { label: "Blog", href: "/blog" },
  { label: "FAQs", href: "/faqs" },
  { label: "About", href: "/about" },
  { label: "Contact", href: "/contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      className={`sticky top-0 z-40 transition-all duration-300 ${scrolled ? "glass-header" : "bg-transparent"}`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2" data-testid="header-logo-link">
          <div className="w-9 h-9 rounded-lg bg-forest grid place-items-center text-bone font-display font-black text-sm">
            IN
          </div>
          <div className="leading-tight">
            <div className="font-display font-bold text-forest text-lg tracking-tight">
              Indiana Accident Aid
            </div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-charcoal-soft font-semibold">
              Help. When you need it most.
            </div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-7" data-testid="desktop-nav">
          {navLinks.map((l) => (
            <Link
              key={l.href}
              to={l.href}
              className={`text-sm font-semibold transition-colors hover:text-terracotta ${
                location.pathname.startsWith(l.href) ? "text-terracotta" : "text-charcoal"
              }`}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={SITE_PHONE_TEL}
            className="inline-flex items-center gap-2 text-sm font-bold text-forest hover:text-terracotta transition-colors"
            data-testid="header-phone-link"
          >
            <Phone className="w-4 h-4" />
            {SITE_PHONE_DISPLAY}
          </a>
          <Link to="/contact" className="btn-primary text-sm py-2.5" data-testid="header-cta-button">
            Free Case Review
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          className="lg:hidden p-2 rounded-md text-forest"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden bg-bone border-t border-black/5 animate-fade-in" data-testid="mobile-menu">
          <nav className="px-6 py-6 flex flex-col gap-1">
            {navLinks.map((l) => (
              <Link
                key={l.href}
                to={l.href}
                className="py-3 text-base font-semibold text-charcoal hover:text-terracotta border-b border-black/5"
                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
              </Link>
            ))}
            <a href={SITE_PHONE_TEL} className="btn-secondary mt-4" data-testid="mobile-call-button">
              <Phone className="w-4 h-4" /> Call {SITE_PHONE_DISPLAY}
            </a>
            <Link to="/contact" className="btn-primary mt-2" data-testid="mobile-cta-button">
              Free Case Review
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
